/**
 * usePrintLabel — Handles box production: sub-unit packing and label printing.
 * Extracted from MobilePackerView for readability.
 */
import { useLanguage } from '../../../i18n/LanguageContext';
import { getPrintErrorMessage } from '../../../utils/printErrors';
import operatorService from '../../../services/operatorService';
import barcodeService, { computeSerialNumber } from '../../../services/barcodeService';
import { getDateCode } from '../../../utils/dateUtils';
import { generateLabelPreviewHtml, LabelData } from '../../../services/labelPrintService';
import toast from 'react-hot-toast';
import type { EmployeeInfo, SessionInfo } from '../../../types';

interface StationInfo {
  station_id: number;
  station_name: string;
  station_barcode?: string;
  location_code?: string;
  printer_mac_id?: string;
}

interface UsePrintLabelParams {
  session: SessionInfo;
  setSession: React.Dispatch<React.SetStateAction<SessionInfo | null>>;
  employee: EmployeeInfo;
  station: StationInfo;
  weightNum: number;
  isWeightValid: boolean;
  isSessionCompleted: boolean;
  boxCount: number;
  subUnitsPacked: number;
  setSubUnitsPacked: (n: number) => void;
  subUnitIds: number[];
  setSubUnitIds: React.Dispatch<React.SetStateAction<number[]>>;
  printerMac: string | null;
  printerIp: string | null;
  numberOfCopies: number;
  requirePostPrintScan: boolean;
  simulatePrinter: boolean;
  /** WCO-3325: configured facility production location for label company info (not physical station). */
  labelProductionLocation: string;
  setBoxCount: React.Dispatch<React.SetStateAction<number>>;
  setShowTargetReachedModal: (v: boolean) => void;
  setIsLoading: (loading: boolean) => void;
  setStep: (step: 'weight_entry' | 'printing' | 'scan_confirm' | 'success') => void;
  setScanStep: (s: 'badge' | 'label') => void;
  setScannedBadge: (b: string | null) => void;
  setLastPrintedBarcode: (b: string | null) => void;
  setShowLabelPreview: (show: boolean) => void;
  setLabelPreviewHtml: (html: string) => void;
  setShowSubUnitsCompleteModal: (show: boolean) => void;
  setWeight: (w: string) => void;
}

export function usePrintLabel({
  session,
  setSession,
  employee,
  station,
  weightNum,
  isWeightValid,
  isSessionCompleted,
  boxCount,
  subUnitsPacked,
  setSubUnitsPacked,
  subUnitIds,
  setSubUnitIds,
  printerMac,
  printerIp,
  numberOfCopies,
  requirePostPrintScan,
  simulatePrinter,
  labelProductionLocation,
  setBoxCount,
  setShowTargetReachedModal,
  setIsLoading,
  setStep,
  setScanStep,
  setScannedBadge,
  setLastPrintedBarcode,
  setShowLabelPreview,
  setLabelPreviewHtml,
  setShowSubUnitsCompleteModal,
  setWeight,
}: UsePrintLabelParams) {
  const { t } = useLanguage();

  // Handle sub-unit pack button click — persists to DB
  const handlePackSubUnit = async () => {
    if (!session?.item_sub_quantity || !session?.sub_session_number) return;

    const actualWeight = weightNum;

    try {
      const result = await operatorService.createSubUnit({
        sub_session_number: session.sub_session_number,
        actual_weight: actualWeight,
      });

      const newId = result.sub_unit?.id;
      const newCount = subUnitsPacked + 1;
      setSubUnitsPacked(newCount);
      if (newId) setSubUnitIds(prev => [...prev, newId]);

      if (newCount >= session.item_sub_quantity) {
        setShowSubUnitsCompleteModal(true);
      } else {
        toast.success(`${t('mobile.subUnit') || 'Sub-unit'} ${newCount} ${t('mobile.packed') || 'packed'}!`);
      }
    } catch (err: any) {
      const errMsg = err.response?.data?.error || err.response?.data?.message || 'Failed to record sub-unit';
      toast.error(errMsg);
    }
  };

  // Handle print label
  const handlePrintLabel = async () => {
    if (!isWeightValid || !session || !station || !employee) return;

    // Don't allow packing if sub-session is completed
    if (isSessionCompleted) {
      toast.error(t('mobile.sessionCompletedMessage') || 'This session has been completed. You cannot pack more boxes.');
      return;
    }

    setIsLoading(true);
    try {
      const finalWeight = weightNum;

      // Generate barcode using shared utility functions (same as desktop view)
      const dateCode = getDateCode();
      const serialNumber = computeSerialNumber();

      const productSku = session.product_sku!;


      // 1. Generate the barcode string
      const barcodeData = await barcodeService.generateBarcode(
        productSku,
        false, // useSubUnit
        finalWeight,
        dateCode,
        serialNumber
      );


      // 2. Create the box record in production inventory
      // This needs to be done BEFORE confirmation so confirm-box can find it
      // Handle 409 (duplicate barcode) gracefully — box already exists from a previous tap
      try {
        const productionResult = await operatorService.recordProduction({
          session_number: 0, // Will be derived from sub_session_number
          sub_session_number: session.sub_session_number,
          product_sku: productSku,
          actual_weight: finalWeight,
          operator_id: null, // Don't set packed_by yet - let confirm-box do it
          station_id: station.station_id,
          barcode_number: barcodeData.data,
          sub_unit_ids: subUnitIds.length > 0 ? subUnitIds : undefined,
          status: 'pending', // Box is pending until double-scan confirmation
        });

        // Show toast if a previous pending box was auto-cancelled
        if (productionResult.cancelled_pending_box) {
          toast(t('mobile.previousBoxCancelled') || 'Previous unconfirmed box was cancelled', {
            icon: '⚠️',
            duration: 3000,
          });
        }
      } catch (recordErr: any) {
        if (recordErr.response?.status === 409) {
        } else if (recordErr.response?.status === 400 && recordErr.response?.data?.error === 'Session completed') {
          // Session was completed by supervisor - update local state and show error
          setSession(prev => prev ? { ...prev, status: 'completed' } : prev);
          toast.error(t('mobile.sessionCompletedMessage') || 'This session has been completed. You cannot pack more boxes.');
          setIsLoading(false);
          return;
        } else if (recordErr.response?.status === 400 && recordErr.response?.data?.error === 'No operator clocked in') {
          toast.error('An operator must be clocked in before production can start');
          setIsLoading(false);
          return;
        } else {
          throw recordErr;
        }
      }

      setLastPrintedBarcode(barcodeData.data);

      // Send to Zebra printer via backend print-service (network TCP)
      const now = new Date();
      let printSucceeded = false;
      let printErrorMsg = '';

      // Test mode or simulate mode: skip actual printing
      const skipPrint = simulatePrinter;
      if (skipPrint) {
        // Generate label preview HTML for TEST MODE
        const labelData: LabelData = {
          product_name: session.product_name || '',
          product_chinese: session.product_chinese || '',
          product_sku: session.product_sku || '',
          product_ingredient: session.product_ingredient || '',
          lot_number: session.lot_number || '',
          box_number: boxCount + 1,
          barcode_number: barcodeData.data,
          actual_weight: Number(finalWeight).toFixed(2),
          target_weight: Number(session.product_unit_weight || session.unit_weight || finalWeight).toFixed(2),
          weight_unit: 'lbs',
          packed_date: now.toLocaleDateString(),
          packed_time: now.toLocaleTimeString(),
          packed_timestamp: now.toISOString(),
          operator_id: employee.user_id || '',
          operator_name: employee.display_name || '',
          station_number: station.station_id,
          station_name: station.station_name || '',
          raw_material_lot: session.lot_number || '',
          raw_material_type: '',
          production_location: labelProductionLocation,
          qr_data: JSON.stringify({
            lot: session.lot_number,
            box: boxCount + 1,
            sku: session.product_sku,
            weight: finalWeight.toFixed(2),
            date: now.toISOString(),
          }),
        };
        const previewHtml = await generateLabelPreviewHtml(labelData, barcodeData, false);
        setLabelPreviewHtml(previewHtml);
        setShowLabelPreview(true);
        toast.success(simulatePrinter ? t('admin.simulatePrinterPrintSkipped') || 'Simulate mode: Print skipped' : 'TEST MODE: Print skipped - showing preview');
        printSucceeded = true;
      } else try {
        const printResult = await operatorService.printLabel({
          label_data: {
            product_name: session.product_name,
            product_chinese: session.product_chinese || '',
            product_sku: session.product_sku,
            product_ingredient: session.product_ingredient || '',
            lot_number: session.lot_number,
            box_number: boxCount + 1,
            barcode_number: barcodeData.data,
            actual_weight: Number(weightNum).toFixed(2),
            target_weight: Number(session.product_unit_weight || session.unit_weight || weightNum).toFixed(2),
            weight_unit: 'lbs',
            packed_date: now.toLocaleDateString(),
            packed_time: now.toLocaleTimeString(),
            packed_timestamp: now.toISOString(),
            operator_id: employee.user_id || '',
            operator_name: employee.display_name || '',
            station_number: station.station_id,
            station_name: station.station_name || '',
            raw_material_lot: session.lot_number,
            raw_material_type: '',
            production_location: labelProductionLocation,
            qr_data: JSON.stringify({
              lot: session.lot_number,
              box: boxCount + 1,
              sku: session.product_sku,
              weight: finalWeight.toFixed(2),
              date: now.toISOString(),
            }),
          },
          barcode_data: barcodeData.data,
          barcode_display_text: barcodeData.displayText || barcodeData.data,
          copies: numberOfCopies,
          station_id: station?.station_id,
          printer_mac: printerMac || undefined,
          printer_ip: printerIp || undefined,
        });
        if (printResult.success) {
          printSucceeded = true;
          toast.success(t('mobile.labelPrinted') || 'Label printed!');
        } else {
          toast.error(getPrintErrorMessage(t, printResult.error_code, printResult.error, 'Print failed'));
        }
      } catch (printErr: any) {
        console.warn('[MobilePackerView] Print service error:', printErr.message);
        const errData = printErr.response?.data;
        printErrorMsg = getPrintErrorMessage(t, errData?.error_code, errData?.error);
      }

      if (printSucceeded) {
        if (requirePostPrintScan && !simulatePrinter) {
          // Print worked — proceed to scan confirmation step (skip when simulating)
          setStep('scan_confirm');
          setScanStep('badge');
          setScannedBadge(null);
        } else {
          // Post-print scan disabled — auto-confirm the box via backend
          try {
            const badgeCode = `@+${employee.employee_number}`;
            const result = await operatorService.confirmBoxScan({
              badge_code: badgeCode,
              box_label: barcodeData!.data,
              sub_session_number: session.sub_session_number!,
            });

            if (result.success) {
              setStep('success');
              toast.success(t('mobile.boxConfirmed') || 'Box confirmed!');
              const backendBoxCount = result.progress?.current_boxes;
              if (backendBoxCount !== undefined) {
                const parsed = typeof backendBoxCount === 'string' ? parseInt(backendBoxCount, 10) : backendBoxCount;
                setBoxCount(parsed);
              } else {
                setBoxCount(prev => prev + 1);
              }

              const autoResetDelay = 3000;
              setTimeout(() => {
                setStep('weight_entry');
                setWeight('');
                setScanStep('badge');
                setScannedBadge(null);
                setSubUnitsPacked(0);
                setSubUnitIds([]);
                if (result.progress?.target_reached) {
                  setShowTargetReachedModal(true);
                }
              }, autoResetDelay);
            } else {
              toast.error(result.message || 'Box confirmation failed');
              setStep('weight_entry');
            }
          } catch (confirmErr: any) {
            console.warn('[usePrintLabel] Auto-confirm failed:', confirmErr.message);
            toast.error(confirmErr.response?.data?.message || 'Box confirmation failed');
            setStep('weight_entry');
          }
        }
      } else {
        // Show specific print service error, or generic fallback
        toast.error(printErrorMsg || t('mobile.printerUnavailableCannotProceed') || 'Printer unavailable — cannot print label. Please check printer connection.');

        // Cleanup order matters: sub-units reference the pending box via parent_box_id,
        // so they must be deleted first to clear the FK before the box can be removed.
        if (subUnitIds.length > 0) {
          await Promise.allSettled(subUnitIds.map(id => operatorService.deleteSubUnit(id)));
          setSubUnitIds([]);
          setSubUnitsPacked(0);
        }
        if (barcodeData?.data) {
          try {
            await operatorService.deletePendingBox(barcodeData.data);
          } catch (cleanupErr) {
            console.warn('[MobilePackerView] Failed to delete pending box:', cleanupErr);
          }
        }
        setLastPrintedBarcode(null);
        // Stay on weight entry step, user can retry when printer is available
      }
    } catch (err: any) {
      console.error('[MobilePackerView] Print error:', err);
      toast.error(err.response?.data?.message || 'Failed to print label');
    } finally {
      setIsLoading(false);
    }
  };

  return { handlePackSubUnit, handlePrintLabel };
}
